// Port of ios/Sources/ViewModels/PeoplePickerPresentation.swift.
//
// The pure half of "start a conversation": who is offered, who is chosen, and what a choice
// means (shared/API.md §19.2). The endpoint follows from the COUNT, decided here rather than
// at the network call: Mattermost's `/channels/group` rejects two ids, because two people is
// a direct message.
import { swiftStringLess, userDisplayName } from './channelListDerivation';
import type { UserRecord } from './records';

/** Mattermost's group-message ceiling is 8 accounts INCLUDING you. */
export const maxOthers = 7;

/**
 * What the current selection would open. 'none' — nothing selected: the confirm action is
 * unavailable, not a broken tap. 'direct' — one other person: `POST /channels/direct`.
 * 'group' — two to `maxOthers` others: `POST /channels/group`.
 */
export type Target = 'none' | 'direct' | 'group';

/**
 * One candidate as the sheet draws it. Selection is carried on the row so the list never has to
 * consult a second set.
 */
export interface Row {
  userId: string;
  name: string;
  username: string;
  lastPictureUpdate: number;
  isSelected: boolean;
  /**
   * A deactivated account (shared/API.md §2.4). The row is still DRAWN — silently omitting a
   * colleague reads as the app losing them — but it is marked and cannot be chosen, because the
   * server would reject the conversation.
   */
  isDeactivated: boolean;
  /** The reader themselves — Mattermost's personal space (shared/API.md §19.2). */
  isSelf: boolean;
}

/** Swift's `UserRecord.isDeactivated`: `delete_at != 0` (shared/API.md §2.4). */
export function isDeactivated(user: Pick<UserRecord, 'deleteAt'>): boolean {
  return user.deleteAt !== 0;
}

export function target(selectedCount: number): Target {
  if (selectedCount <= 0) return 'none';
  return selectedCount === 1 ? 'direct' : 'group';
}

export function canSelectMore(selectedCount: number): boolean {
  return selectedCount < maxOthers;
}

/**
 * Selection order is preserved — the chips above the field are read as a sentence. Selecting
 * past the ceiling is a NO-OP rather than an error: the rows past it are already drawn
 * unavailable, so a tap that got through is a race, not a mistake.
 */
export function toggle(selected: readonly string[], userId: string): string[] {
  if (selected.includes(userId)) return selected.filter((id) => id !== userId);
  if (!canSelectMore(selected.length)) return [...selected];
  return [...selected, userId];
}

/**
 * Whether `row` can be chosen given what is already chosen. A conversation with yourself is a
 * conversation of ONE — yourself and everyone else are mutually exclusive.
 */
export function isSelectable(row: Row, selected: readonly string[], selfUserId: string): boolean {
  if (row.isDeactivated) return false;
  if (selected.includes(row.userId)) return true;
  if (row.isSelf) return selected.length === 0;
  if (selected.includes(selfUserId)) return false;
  return canSelectMore(selected.length);
}

/** Swift's `String <` (Unicode scalars of the NFC form), not JS code-unit order. */
const less = swiftStringLess;

/**
 * Candidates as rows, under the reader's teammate-name-display policy. `currentUserId` is dropped
 * unless `includeSelf` (the personal space belongs in the list that STARTS conversations,
 * nowhere else). Two sort keys, not one: people who share a display name would otherwise reorder
 * between keystrokes. Deactivated people sort BELOW everyone active, as a block. Yourself first:
 * the personal space is a destination people go to deliberately.
 */
export function rows(args: {
  candidates: readonly UserRecord[];
  currentUserId: string;
  selected: readonly string[];
  includeSelf?: boolean;
}): Row[] {
  const { candidates, currentUserId, selected } = args;
  const includeSelf = args.includeSelf ?? false;
  const seen = new Set<string>();
  const out: Row[] = [];
  for (const user of candidates) {
    if (!includeSelf && user.id === currentUserId) continue;
    if (seen.has(user.id)) continue;
    seen.add(user.id);
    out.push({
      userId: user.id,
      name: userDisplayName(user),
      username: user.username,
      lastPictureUpdate: user.lastPictureUpdate,
      isSelected: selected.includes(user.id),
      isDeactivated: isDeactivated(user),
      isSelf: user.id === currentUserId,
    });
  }
  return out.sort((a, b) => {
    if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
    if (a.isDeactivated !== b.isDeactivated) return b.isDeactivated ? -1 : 1;
    const na = a.name.toLowerCase();
    const nb = b.name.toLowerCase();
    if (na !== nb) return less(na, nb) ? -1 : 1;
    if (a.username !== b.username) return less(a.username, b.username) ? -1 : 1;
    if (a.userId !== b.userId) return less(a.userId, b.userId) ? -1 : 1;
    return 0;
  });
}

/**
 * The chosen people, in the order they were chosen, resolved from everyone the sheet has SEEN —
 * a selected person who has since dropped out of the visible candidate list (the ordinary result
 * of typing another query) must still have a chip.
 */
export function chips(selected: readonly string[], known: ReadonlyMap<string, UserRecord>): Row[] {
  const out: Row[] = [];
  for (const id of selected) {
    const user = known.get(id);
    if (!user) continue;
    out.push({
      userId: user.id,
      name: userDisplayName(user),
      username: user.username,
      lastPictureUpdate: user.lastPictureUpdate,
      isSelected: true,
      isDeactivated: false,
      isSelf: false,
    });
  }
  return out;
}

export const PeoplePickerPresentation = { maxOthers, target, canSelectMore, toggle, isSelectable, rows, chips, isDeactivated } as const;
